import { addDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import type { VacationOverviewDocument } from '../../models/vacationOverview.types';
import { toIsoDay, parseIsoDay } from '../overviewDates';
import { periodPhase } from './panoramaPeriodPhase';
import { openRequestSegments, isApprovalUrgent, approvalDeadlineIso } from './panoramaRequestItems';
import type { DisplayDate } from './panoramaDisplayDates';

export interface PeriodRange {
	from: string;
	to: string;
}

export type PeriodSituation = 'on-vacation' | 'scheduled' | 'pending' | null;

const PENDING_STATUSES = ['PendingManager', 'PendingClient', 'PendingDP', 'PendingCorrection'];

export function periodSituation(
	doc: VacationOverviewDocument,
	range: PeriodRange,
	todayIso: string
): PeriodSituation {
	const hasOnVacation = doc.periods.some(
		p => (periodPhase(p, todayIso) === 'current' || periodPhase(p, todayIso) === 'past') &&
			p.startDate <= range.to && p.endDate >= range.from
	);

	if (hasOnVacation) return 'on-vacation';

	const hasScheduled = doc.periods.some(
		p => periodPhase(p, todayIso) === 'future' &&
			p.startDate <= range.to && p.endDate >= range.from
	);

	if (hasScheduled) return 'scheduled';

	const hasPending = doc.openRequest && PENDING_STATUSES.includes(doc.openRequest.status) &&
		openRequestSegments(doc.openRequest).some(
			seg => seg.startDate <= range.to && seg.endDate >= range.from
		);

	if (hasPending) return 'pending';

	return null;
}

export function periodDisplayDates(
	doc: VacationOverviewDocument,
	range: PeriodRange,
	todayIso: string
): DisplayDate[] {
	const items: DisplayDate[] = [];

	doc.periods
		.filter(p => p.startDate <= range.to && p.endDate >= range.from)
		.forEach(p => {
			const phase = periodPhase(p, todayIso);
			if (phase === 'current' || phase === 'past') {
				items.push({
					startDate: p.startDate,
					endDate: p.endDate,
					days: p.days,
					kind: 'current',
					tone: 'default',
				});
			} else if (phase === 'future') {
				items.push({
					startDate: p.startDate,
					endDate: p.endDate,
					days: p.days,
					kind: 'scheduled',
					tone: 'default',
				});
			}
		});

	if (doc.openRequest && PENDING_STATUSES.includes(doc.openRequest.status)) {
		const segments = openRequestSegments(doc.openRequest).filter(
			seg => seg.startDate <= range.to && seg.endDate >= range.from
		);
		const total = openRequestSegments(doc.openRequest).length;
		segments.forEach(seg => {
			items.push({
				startDate: seg.startDate,
				endDate: seg.endDate,
				days: seg.days,
				kind: seg.decision === 'approved' ? 'request-approved' : 'request',
				tone: 'default',
				fraction: total > 1 ? seg.fraction : undefined,
			});
		});
	}

	return items;
}

type PresetKey = 'next-30' | 'next-90' | 'next-180' | 'this-month' | 'next-month' | 'last-month';

export function presetRange(preset: PresetKey, today: Date): PeriodRange {
	const todayIso = toIsoDay(today);

	switch (preset) {
		case 'next-30':
			return { from: todayIso, to: toIsoDay(addDays(today, 30)) };
		case 'next-90':
			return { from: todayIso, to: toIsoDay(addDays(today, 90)) };
		case 'next-180':
			return { from: todayIso, to: toIsoDay(addDays(today, 180)) };
		case 'this-month': {
			const start = startOfMonth(today);
			const end = endOfMonth(today);
			return { from: toIsoDay(start), to: toIsoDay(end) };
		}
		case 'next-month': {
			const nextMonth = addDays(endOfMonth(today), 1);
			const start = startOfMonth(nextMonth);
			const end = endOfMonth(nextMonth);
			return { from: toIsoDay(start), to: toIsoDay(end) };
		}
		case 'last-month': {
			const lastMonth = subMonths(startOfMonth(today), 1);
			const start = startOfMonth(lastMonth);
			const end = endOfMonth(lastMonth);
			return { from: toIsoDay(start), to: toIsoDay(end) };
		}
	}
}

export function validateRange(range: PeriodRange | null, minDate?: string): boolean {
	if (!range) return false;
	if (range.from > range.to) return false;
	if (minDate && range.from < minDate) return false;
	try {
		const fromDate = parseIsoDay(range.from);
		const toDate = parseIsoDay(range.to);
		if (!fromDate || !toDate) return false;
		const daysDiff = Math.floor((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
		if (daysDiff > 366) return false;
		return true;
	} catch {
		return false;
	}
}

export function earliestVacationDate(documents: VacationOverviewDocument[]): string | null {
	let earliest = '9999-12-31';
	let found = false;

	documents.forEach(doc => {
		if (!doc.active || !doc.inBase) return;

		doc.periods.forEach(p => {
			if (p.startDate < earliest) {
				earliest = p.startDate;
				found = true;
			}
		});

		if (doc.openRequest) {
			const segments = openRequestSegments(doc.openRequest);
			segments.forEach(seg => {
				if (seg.startDate < earliest) {
					earliest = seg.startDate;
					found = true;
				}
			});
		}
	});

	return found ? earliest : null;
}


export function resolvePeriodStatus(
	doc: VacationOverviewDocument,
	todayStatus: string,
	range: PeriodRange,
	todayIso: string,
	daysToConcessive: number | null,
	concessiveDeadline: string | null,
	includeApprovalDeadline = false
): string | null {
	const ov = (start: string, end: string) => start <= range.to && end >= range.from;
	const EXPIRING_WINDOW_DAYS = 90;

	const hasCurrentVacation = doc.periods.some(
		p => (periodPhase(p, todayIso) === 'current' || periodPhase(p, todayIso) === 'past') &&
			ov(p.startDate, p.endDate)
	);
	if (hasCurrentVacation) return 'on-vacation';

	if (doc.openRequest && PENDING_STATUSES.includes(doc.openRequest.status)) {
		const segments = openRequestSegments(doc.openRequest);
		if (segments.some(seg => ov(seg.startDate, seg.endDate))) {
			return isApprovalUrgent(doc.openRequest, new Date(todayIso + 'T00:00:00')) ? 'approval-urgent' : 'pending';
		}
	}

	if (doc.periods.some(p => periodPhase(p, todayIso) === 'future' && ov(p.startDate, p.endDate))) {
		return 'scheduled';
	}

	const legalDeadline = doc.balance?.nextLegalDeadline ?? null;
	if ((doc.balance?.expiredDays ?? 0) > 0 || (legalDeadline && legalDeadline < todayIso)) {
		return 'expired';
	}

	if (concessiveDeadline && ov(concessiveDeadline, concessiveDeadline) && daysToConcessive !== null && daysToConcessive <= EXPIRING_WINDOW_DAYS) {
		return 'expiring-soon';
	}

	if (concessiveDeadline && ov(concessiveDeadline, concessiveDeadline)) {
		return 'none';
	}

	if (includeApprovalDeadline && doc.openRequest && PENDING_STATUSES.includes(doc.openRequest.status)) {
		const approvalDl = approvalDeadlineIso(doc.openRequest);
		if (approvalDl && ov(approvalDl, approvalDl)) {
			return isApprovalUrgent(doc.openRequest, new Date(todayIso + 'T00:00:00')) ? 'approval-urgent' : 'pending';
		}
	}

	if (todayStatus === 'acquiring') {
		return 'acquiring';
	}

	return null;
}
